/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * BROWSE-47 — Store resolution waterfall + delivery-ZIP reconciliation.
 *
 * Owns: cookie names, TTLs, URL-param names, default-zone fallback, the
 * waterfall source enum, and the reconciliation flash codes. Anything that
 * needs to be shared between the middleware, action routes, the toast
 * component, and tests imports from here.
 */

export const ASH_STORE_COOKIE_NAME = 'ash-store';
export const ASH_DELIVERY_ZIP_COOKIE_NAME = 'ash-delivery-zip';
/**
 * SSF-558 — short-lived cache for the IP-geo tier of the waterfall.
 *
 * The geo tier resolves the shopper's ZIP via a Shopper Context PUT with
 * `clientIp`, which costs one SCAPI round-trip (~200–500ms). Left unbounded
 * it would fire on every SSR request on any session that never persists an
 * `ash-store` cookie (e.g. the geo lookup itself never persists, and neither
 * does the DEFAULT fallback). This cookie caches the outcome — resolved ZIP
 * OR the sentinel empty-string "we tried and got nothing" — so the SC call
 * runs at most once per shopper per hour.
 *
 * TTL matches legacy's per-session semantics: legacy's `ZipManager` cached
 * the geo lookup on `session.custom.customerZip` for the browser session; an
 * hour is the shortest interval that keeps the SC round-trip off the hot
 * path while still refreshing often enough that a shopper who moves (VPN,
 * travel) re-personalizes within a reasonable window.
 */
export const ASH_GEO_CACHE_COOKIE_NAME = 'ash-geo';
export const ASH_GEO_CACHE_COOKIE_EXPIRY_SECONDS = 60 * 60;
/**
 * Non-empty sentinel value written to `ash-geo` when the Shopper Context call
 * completed but returned no useful US postal (non-US country, missing
 * `x-geolocation`, error). Must be non-empty because `createCookie.parse`
 * short-circuits empty values to `null` (indistinguishable from "no cookie"),
 * and must not match `ZIP_CODE_REGEX` so it can never be mistaken for a ZIP.
 */
export const ASH_GEO_CACHE_MISS_SENTINEL = '-';

export const ASH_STORE_COOKIE_EXPIRY_SECONDS = 180 * 24 * 60 * 60;
export const ASH_DELIVERY_ZIP_COOKIE_EXPIRY_SECONDS = 180 * 24 * 60 * 60;

/**
 * The delivery-ZIP cookie Ashley NEXT / SG owns (`org_ashcomm` ZipManager
 * `setZipInCookie`). Shared verbatim with the legacy apps, which cannot be
 * changed: bare name (no `_Ashley-US` namespace), bare 5-digit value, host-only,
 * and NOT HttpOnly — legacy's client JS reads it.
 *
 * This cookie is the AUTHORITY for *what the shopper's ZIP is* across all three
 * apps. `ash-delivery-zip` remains Storefront Next's private cache of everything
 * derived from it (`priceZone`, `zoneStatus`, `manual`, `setAt`) — values legacy
 * has no concept of and no room for in a bare 5-digit string. When the two
 * disagree, this one wins: since Storefront Next writes it on every ZIP change
 * too, a divergence can only mean Ashley NEXT wrote it more recently.
 *
 * Never a source of price zone. The zone is always re-derived server-side from
 * the ZIP via `ashley-zone-resolve`, so a tampered cookie can do no more than
 * typing a different ZIP into the header widget.
 */
export const LEGACY_ZIP_COOKIE_NAME = 'customerZip';

/**
 * Legacy writes `customerZip` with a 180-day max-age (`ZipManager.js` —
 * `60 * 60 * 24 * 180`). Matched exactly so a Storefront Next write never
 * shortens or extends the lifetime Ashley NEXT expects.
 */
export const LEGACY_ZIP_COOKIE_EXPIRY_SECONDS = 180 * 24 * 60 * 60;

/** Header set by middleware on responses where reconciliation produced a flash message. */
export const ZIP_FLASH_HEADER = 'x-zip-flash';

/**
 * Display label for a present-but-unmapped ZIP (legacy NON_MAPPED_ZIP_PRICE_ZONE).
 * Not a price-book selector — the AFHSDPG assignment is disabled, so an unmapped
 * shopper prices from the site-default books (legacy parity).
 */
export const DEFAULT_PRICE_ZONE = 'AFHSDPG';

/**
 * Price zone forced by the Merkle / Google-Shopping marketing deep link
 * (`?mrkgcl=…&product_id=…`) when the shopper has no ZIP or region yet.
 * Legacy parity: org_ashcomm Product.js `show()` activates `usd-AFHSPGZ1-*`
 * ("Zone 1") for a Merkle landing with no zip/region. Note this is a PRICE ZONE
 * only — legacy assigns NO physical store for the bare-Merkle case.
 */
export const MERKLE_PRICE_ZONE = 'AFHSPGZ1';

/** Hard-coded default store ID for the AFHSDPG fallback path. */
export const DEFAULT_STORE_ID = 'AFHSDPG';

/**
 * URL parameter names that seed the waterfall on inbound requests.
 * Verbatim legacy names — marketing/email/store-locator deep links use these.
 *
 * `store_code`, `region_id`, and the Merkle pair (`mrkgcl` + `product_id`) are
 * the verified legacy deep-link params (org_ashcomm Product.js / ProductUtils
 * `verifyUrlHasMerkleParameter`). `postal_code` is NOT a legacy param — it is a
 * net-new Storefront Next deep-link entry point added for parity with the
 * customer flow diagram.
 */
export const URL_PARAM_STORE_CODE = 'store_code';
export const URL_PARAM_POSTAL_CODE = 'postal_code';
export const URL_PARAM_REGION_ID = 'region_id';
/** Merkle / Google-Shopping marketing param. Only honoured when paired with `product_id`. */
export const URL_PARAM_MERKLE = 'mrkgcl';
export const URL_PARAM_PRODUCT_ID = 'product_id';

/** US ZIP code regex — 5 digits or 5+4. */
export const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;

/**
 * Source attribution on the resolved store. Tells the reconciler whether the
 * resolution was deep-linked (URL params), persisted (cookie), profile-driven,
 * geolocated, or hard-coded default. Geolocated stores are NOT persisted to
 * the long-lived ash-store cookie — they recompute every visit.
 */
export const STORE_SOURCE = {
    STORE_CODE: 'store_code',
    POSTAL_CODE: 'postal_code',
    REGION_ID: 'region_id',
    MERKLE: 'merkle',
    COOKIE: 'cookie',
    /** Resolved from the shared `customerZip` cookie after Ashley NEXT / SG changed it. */
    LEGACY_ZIP: 'legacy_zip',
    PROFILE: 'profile',
    GEO: 'geo',
    DEFAULT: 'default',
    MANUAL: 'manual',
} as const;
export type StoreSource = (typeof STORE_SOURCE)[keyof typeof STORE_SOURCE];

/**
 * Flash message codes set on the response by the reconciler. Consumed by
 * <ZipUpdateToast> via the x-zip-flash response header.
 *
 * Per the customer flow diagram (the authoritative source), the notification
 * fires ONLY when a shopper's MANUALLY-entered delivery ZIP is overwritten
 * because the newly resolved store is in a DIFFERENT price zone (Step C, manual
 * branch). Same-zone resolutions retain the prior ZIP silently, and an
 * auto-derived ZIP overwritten cross-zone updates silently.
 */
export const ZIP_FLASH = {
    ZONE_UPDATED: 'zone-updated',
} as const;
export type ZipFlashCode = (typeof ZIP_FLASH)[keyof typeof ZIP_FLASH];
